/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { X_STEP_UP, Y_STEP_UP } from 'constants/map';
import { Point } from '../types';
import { Direction, NodeList } from './types';

export const isSamePoint = (source: Point, target: Point) => source.x === target.x && source.y === target.y;

export const getDirection = ({ source, target }: { source: Point; target: Point }): Direction => ({
    x: target.x - source.x > 0 ? 1 : target.x - source.x < 0 ? -1 : 0,
    y: target.y - source.y > 0 ? 1 : target.y - source.y < 0 ? -1 : 0
});

const isTurn = (path: { x: number; y: number }[], index: number) => {
    const current = path[index];
    const previous = path[index - 1];
    const next = path[index + 1];

    if (!previous || !next || !current) return false;

    const direction = getDirection({ source: previous, target: current });
    const nextDirection = getDirection({ source: current, target: next });

    return direction.x !== nextDirection.x || direction.y !== nextDirection.y;
};

export const getNodesList = (stations: Point[]) =>
    stations.reduce(
        (acc, { x, y }) => {
            if (acc.nodeX[x]) {
                acc.nodeX[x].push({ point: y });
            } else {
                acc.nodeX[x] = [{ point: y }];
            }
            if (acc.nodeY[y]) {
                acc.nodeY[y].push({ point: x });
            } else {
                acc.nodeY[y] = [{ point: x }];
            }
            return acc;
        },
        { nodeX: {}, nodeY: {} } as NodeList
    );

const getPointDirection = (path: { x: number; y: number }[], index: number) => {
    if (index === 0) return { x: 0, y: 0 };
    if (isTurn(path, index)) return { x: 0, y: 0 };
    return getDirection({ source: path[index - 1], target: path[index] });
};

export const getPathNodeList = (path: Point[]) =>
    path.reduce(
        (acc, { x, y }, index) => {
            if (acc.nodeX[x]) {
                acc.nodeX[x].push({ point: y, direction: getPointDirection(path, index).y });
            } else {
                acc.nodeX[x] = [{ point: y, direction: getPointDirection(path, index).y }];
            }
            if (acc.nodeY[y]) {
                acc.nodeY[y].push({ point: x, direction: getPointDirection(path, index).x });
            } else {
                acc.nodeY[y] = [{ point: x, direction: getPointDirection(path, index).x }];
            }
            return acc;
        },
        { nodeX: {}, nodeY: {} } as {
            nodeX: { [key: number]: Array<{ point: number; direction: number }> };
            nodeY: { [key: number]: Array<{ point: number; direction: number }> };
        }
    );

export const mergeNodeList = ({ source, target }: { source: NodeList; target: NodeList }) => {
    const nodeList = { ...target } as NodeList;

    Object.entries(source).forEach(([nodeKey, nodeValues]) => {
        Object.entries(nodeValues).forEach(([level, values]: [string | number, Array<{ point: number; direction?: number }>]) => {
            nodeList[nodeKey as 'nodeX' | 'nodeY'][level as number] = values
                .concat(target[nodeKey as 'nodeX' | 'nodeY'][level as number] || [])
                .reduce((acc, value) => {
                    if (!acc.find(x => x.point === value.point)) {
                        acc.push(value);
                    }
                    return acc;
                }, [] as Array<{ point: number; direction?: number }>);
        });
    });

    return nodeList;
};

export const checkInBetweenNodesInXDirection = (params: {
    source: Point;
    target: Point;
    direction: Point;
    nodeList: NodeList;
    count?: number;
    checkPathInOppositeDirection?: boolean;
}) => {
    const { source, target, direction, nodeList, count = 1, checkPathInOppositeDirection } = params;
    const oppositeDirection = checkPathInOppositeDirection ? -direction.x : direction.x;
    return (
        nodeList.nodeY[source.y] &&
        nodeList.nodeY[source.y]
            .sort((a, b) => a.point - b.point)
            .filter(x =>
                direction.x > 0
                    ? x.point > source.x && x.point < target.x && (x.direction ? x.direction === oppositeDirection : true)
                    : x.point < source.x && x.point > target.x && (x.direction ? x.direction === oppositeDirection : true)
            ).length >= count
    );
};

export const checkInBetweenNodesInYDirection = (params: {
    source: Point;
    target: Point;
    direction: Point;
    nodeList: NodeList;
    count?: number;
}) => {
    const { source, target, direction, nodeList, count = 1 } = params;
    return (
        nodeList.nodeX[source.x] &&
        Array.from(new Set(nodeList.nodeX[source.x]))
            .sort((a, b) => a.point - b.point)
            .filter(y => (direction.y > 0 ? y.point > source.y && y.point < target.y : y.point < source.y && y.point > target.y)).length >=
            count
    );
};

export const moveIntoDirection = (params: { source: Point; direction: Point; step: number }) => {
    const { source, direction, step } = params;
    return { x: source.x + direction.x * step, y: source.y + direction.y * step };
};

export const moveDown = (source: Point) =>
    moveIntoDirection({
        source,
        direction: { x: 0, y: 1 },
        step: Y_STEP_UP
    });

export const moveHorizontal = (source: Point, direction: number, step = X_STEP_UP) =>
    moveIntoDirection({
        source,
        step,
        direction: { x: direction, y: 0 }
    });

export const moveVertical = (source: Point, direction: number, step = Y_STEP_UP) =>
    moveIntoDirection({
        source,
        step,
        direction: { x: 0, y: direction }
    });
